import { useTranslation } from 'next-i18next';
import { useEffect, useMemo, useState } from 'react';

import { NS } from '@/constants';
import { DeliverySlotDataModel } from '@/models';
import { useAppDispatch, useAppSelector } from '@/redux';
import { isObjectEmpty } from '@/utils/comparisonUtils';
import { formatKebabCaseDate, formatLiteralDate, formatSlotToLocaleString } from '@/utils/dateUtils';

import { updateDeliverySlotData } from '@/redux/slices/cart.slice';
import { updateDeliverySlotDataExpro } from '@/redux/slices/expro.slice';
import { toggleDeliveryScheduleModal } from '@/redux/slices/gui.slice';

import TimeSlotSelector from '@/components/checkout/shipMode/DeliverySelector/TimeSlotSelector';
import CustomCalendar from '@/components/common/calendar/CustomCalendar';

import styles from './DeliveryScheduleDateSelector.module.scss';

const FORM_ID = 'timeslot';

type DeliveryScheduleDateSelectorProps = {
  data: any;
  onSelectedSlot: (slot: {
    phoneNumber: string,
    start: string,
    end: string
  }) => void,
  withPadding: boolean,
  isExproCheckout: boolean,
  isDayOnly: boolean
}

export const DeliveryScheduleDateSelector = (
  { data, onSelectedSlot, withPadding, isExproCheckout, isDayOnly }: DeliveryScheduleDateSelectorProps
) : React.JSX.Element => {
  const { t } = useTranslation(NS.CHECKOUT);
  const dispatch = useAppDispatch();

  const deliverySlotData = useAppSelector(({ cart }) => cart.deliverySlotData);
  const deliverySlotDataExpro = useAppSelector(({ expro }) => expro.deliverySlotDataExpro);
  const locale = useAppSelector(({ root }) => root.storeConf.codeISO.toLowerCase());

  const [bookingSlot, setBookinSlot] = useState(isExproCheckout ? deliverySlotDataExpro : deliverySlotData);
  const [selectedDay, setSelectedDay] = useState<string>();
  const [slots, setSlots] = useState([]);
  const [daysName, setDaysName] = useState([]);

  const containerClassName = [
    styles.container,
    withPadding && styles['with-padding']
  ].filter(Boolean).join(' ');

  /**
   * @param {object} slot
   * @return {list} list of short day names depends on local (ex: ['lun.', 'mar.'])
   */
  const getDaysName = (slot) => (
    slot.map((day) => (
      new Date(day?.dayOfShipping).toLocaleDateString(locale, { weekday: 'short' })
    ))
  );

  useEffect(() => {
    setSlots(data);

    if (data.length > 0) {
      setDaysName(getDaysName(data[0]));
    }

    if (isObjectEmpty(bookingSlot)) {
      const firstDayAvailable = data?.flat().find((day) => day.shippingPossible === true)?.dayOfShipping;
      setSelectedDay(formatKebabCaseDate(firstDayAvailable, locale));
    }
  }, []);

  useEffect(() => {
    if (!isObjectEmpty(bookingSlot)) {
      setSelectedDay(bookingSlot.day);
    }
  }, []);

  useEffect(() => {
    if (!isExproCheckout) {
      setBookinSlot(deliverySlotData);
    }
  }, [deliverySlotData]);

  useEffect(() => {
    if (isExproCheckout) {
      setBookinSlot(deliverySlotDataExpro);
    }
  }, [deliverySlotDataExpro]);

  const selectedSlots = useMemo(() => {
    return slots.flat().find((day) => (
      formatKebabCaseDate(day.dayOfShipping, locale) === selectedDay
    ))?.timeSlots;
  }, [slots, selectedDay]);

  const handleClick = (day) => {
    setSelectedDay(formatKebabCaseDate(day, locale));
  };

  const handleSubmit = (data) => {
    const slot = isDayOnly ?
      `${selectedSlots[0]?.start}|${selectedSlots[0]?.end}` :
      data?.slot;

    const [start, end] = slot?.split('|') ?? [];

    const bookingSlotData = {
      phoneNumber: data.phoneNumber,
      ...formatSlotToLocaleString([start, end]),
      selectedSlot: slot
    } as DeliverySlotDataModel;

    if (isExproCheckout) {
      dispatch(updateDeliverySlotDataExpro({ data: bookingSlotData }));
    } else {
      dispatch(updateDeliverySlotData({ data: bookingSlotData }));
    }

    dispatch(toggleDeliveryScheduleModal({ opened: false, shippingId: undefined }));

    onSelectedSlot({
      phoneNumber: data.phoneNumber,
      start,
      end
    });
  };

  return (
    <>
      <div className={containerClassName} data-test-id="container">
        <span className={styles.subtitle} data-test-id="subtitle">{t('shipMode.topChrono.deliveryDate')} </span>
        <span className={styles['selected-day']} data-test-id="selected-day">
          {selectedDay && formatLiteralDate(selectedDay, locale)}
        </span>
        {daysName && <CustomCalendar
          dayNames={daysName}
          weekList={slots}
          numberOfDisplayedWeeks={4}
          selectedDay={selectedDay}
          onClick={handleClick}
        />}
        {selectedSlots?.length > 0 && (
          <TimeSlotSelector
            t={t}
            formId={FORM_ID}
            timeSlots={selectedSlots}
            onSubmit={handleSubmit}
            defaultValues={{ phoneNumber: bookingSlot.phoneNumber, slot: bookingSlot.selectedSlot }}
            selectedSlot={bookingSlot.selectedSlot}
            isDayOnly={isDayOnly}
          />
        )}
      </div>
    </>
  );
};

export default DeliveryScheduleDateSelector;
